import { createApi } from '@reduxjs/toolkit/query/react'
import {baseQuery} from "../baseQuery";
import AnimeList from "../../models/AnimeList";

type slugInObject = {
    slug: string;
}

const refetchSeconds = process.env.REACT_APP_REFETCH_SECONDS ? parseInt(process.env.REACT_APP_REFETCH_SECONDS) : 30;

export const animeListsApi = createApi({
    reducerPath: 'animeListsApi',
    baseQuery,
    tagTypes: ["AnimeLists", "StatusCounts", "Calendar"],
    keepUnusedDataFor: 300,
    refetchOnMountOrArgChange: refetchSeconds,
    endpoints: (builder) => ({
        getAllLists: builder.query({
            query: () => `anime-lists`,
            providesTags: ['AnimeLists'],
        }),
        getList: builder.query<AnimeList, slugInObject>({
            query: ({slug}) => `anime-lists/${slug}`,
            providesTags:  ['AnimeLists'],
        }),
        getSelectOptions: builder.query({
            query: () => `anime-lists/select-options`,
            providesTags: ["AnimeLists"]
        }),
        getStatusCounts: builder.query<any, slugInObject>({
            query: ({slug}) => `anime-lists/${slug}/status/count`,
            providesTags: ["StatusCounts"]
        }),
        addList: builder.mutation({
            query: ({ ...body }) => ({
                url: `anime-lists`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ["AnimeLists"]
        }),
        deleteList: builder.mutation<any, slugInObject>({
            query: ({slug}) => ({
                url: `anime-lists/${slug}`,
                method: 'DELETE'
            }),
            invalidatesTags: ["AnimeLists"],
        }),
        updateList: builder.mutation({
            query: ({slug, ...body}) => ({
                url: `anime-lists/${slug}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ["AnimeLists"],
        }),
        getAgenda: builder.query<any, slugInObject>({
            query: ({slug}) => `anime-lists/${slug}/agenda`,
            providesTags: ["Calendar"],
        }),
        addListedAnimeToList: builder.mutation({
            query: ({slug, ...body}) => ({
                url: `anime-lists/${slug}/listed-anime-items`,
                method: 'POST',
                body
            })
        }),
        changeLockOfAnimeList: builder.mutation<any, slugInObject>({
            query: ({slug}) => ({
                url: `anime-lists/${slug}/change-lock`,
                method: 'PUT'
            })
        }),
        createListFromSeason: builder.mutation({
            query: ({ ...body }) => ({
                url: `anime-lists/from-season`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ["AnimeLists"],
        }),
        shareList: builder.mutation({
            query: ({slug, ...body}) => ({
                url: `anime-lists/${slug}/share`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ["AnimeLists"],
        }),
        importMal: builder.mutation<any, FormData>({
            query: (formData) => ({
                url: `anime-lists/import/mal`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ["AnimeLists"],
        }),
    }),
})

export const {
    useGetAllListsQuery,
    useGetSelectOptionsQuery,
    useLazyGetSelectOptionsQuery,
    useAddListMutation,
    useDeleteListMutation,
    useUpdateListMutation,
    useLazyGetStatusCountsQuery,
    useLazyGetListQuery,
    useLazyGetAgendaQuery,
    useAddListedAnimeToListMutation,
    useChangeLockOfAnimeListMutation,
    useCreateListFromSeasonMutation,
    useShareListMutation,
    useImportMalMutation,
} = animeListsApi
